import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { LockKeyhole } from 'lucide-react';

// LoginModal — only entry point into RADAR. A pure modal with a single
// "Sign in with Google" action. Clicking redirects to Emergent's hosted
// Google OAuth screen. The redirect target is derived from
// `window.location.origin` at click-time so preview vs production each
// returns to itself — never hardcode.
//
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function LoginModal({ open = true }) {
  const startGoogleLogin = () => {
    // The OAuth flow drops the user at `${origin}/auth/callback#session_id=...`
    // which our router catches synchronously (see App.js + AuthCallback).
    const redirectUrl = `${window.location.origin}/auth/callback`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <Dialog open={open}>
      <DialogContent
        className="sm:max-w-md p-0 overflow-hidden"
        data-testid="login-modal"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <div className="bg-wv-ink text-white px-8 py-7">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-md bg-primary/15 flex items-center justify-center ring-1 ring-primary/40">
              <LockKeyhole className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.22em] text-primary/70 font-semibold">Wellversed</p>
              <p className="text-base font-semibold leading-tight">RADAR</p>
            </div>
          </div>
        </div>

        <div className="px-8 py-7 space-y-5 bg-card">
          <DialogHeader className="space-y-2 text-left">
            <DialogTitle className="text-xl font-semibold tracking-tight text-wv-ink">Sign in to continue</DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground leading-relaxed">
              Use your <span className="font-semibold text-wv-ink">@wellversed.in</span> Google account to access RADAR. Access is restricted to verified Wellversed employees.
            </DialogDescription>
          </DialogHeader>

          <Button
            type="button"
            onClick={startGoogleLogin}
            className="w-full h-11 text-sm font-medium"
            data-testid="google-signin-button"
          >
            <svg className="w-4 h-4 mr-2" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.6 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/>
              <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 7 29.3 5 24 5 16 5 9.2 9.6 6.3 14.7z"/>
              <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.5-5.2l-6.3-5.3C29.3 35 26.8 36 24 36c-5.3 0-9.7-3.4-11.3-8L6.2 32.6C9 38.3 16 44 24 44z"/>
              <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.2 4.3-4.1 5.7l6.3 5.3C41.8 35.3 44 30 44 24c0-1.3-.1-2.4-.4-3.5z"/>
            </svg>
            Sign in with Google
          </Button>

          <p className="text-[11px] text-muted-foreground/80 text-center pt-1">
            Access is restricted to verified @wellversed.in employees.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
